/*
 * Copyright (c) CIRROM 2023.
 */

import { ContactsTimeline } from "../../api/integratedsystems";
import React, { useState } from "react";
import { a, useSpring } from "@react-spring/web";
import classes from "./NewCRMPanel.module.css";
import Typography from "@mui/material/Typography";
import { NewCRMPanelBarSection } from "./CRMPanelBarSection";
import { getCrmScoreStyles } from "../utils/utils";
import CircleIcon from "@mui/icons-material/Circle";
export const CRMHealthHistoryPanelBarSection = (props: {
  changeMetrics: any;
  series?: string[];
  alternativeChartVariant?: boolean;
  maxHeight?: number;
}) => {
  return (
    <NewCRMPanelBarSection
      changeMetrics={props.changeMetrics}
      series={props.series}
      alternativeChartVariant={props.alternativeChartVariant}
      maxHeight={props.maxHeight}
      legendData={[
        {
          id: "total_contacts",
          label: "Total",
          color: "#0090c3",
        },
        {
          id: "contacts_with_issues",
          label: "w. Issues",
          color: "#e50144",
        },
      ]}
    />
  );
};

const HealthScoreExplain = () => {
  return (
    <div style={{ display: "flex", justifyContent: "space-evenly" }}>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#e50144" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          0-35%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#e65100" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          35-45%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#f57c00" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          45-55%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#ffa000" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          55-65%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#fbc02d" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          65-76%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#b5c332" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          76-86%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#8bc34a" }} />
        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          86-92%
        </Typography>
      </div>
      <div className={classes.circleProgression}>
        <CircleIcon fontSize={"small"} style={{ color: "#34be5d" }} />

        <Typography variant={"caption"} style={{ fontSize: "0.45rem" }}>
          92-100%
        </Typography>
      </div>
    </div>
  );
};

export function CrmHealthScore(props: {
  scoreStats: any;
  contactsTimeline: ContactsTimeline[];
  isDataProject?: boolean;
}) {
  /**
   * Panel displaying the start and latest score
   *
   * */
  const [displayContactsGraph, setDisplayContactsGraph] =
    useState<boolean>(false);
  const { transform, opacity } = useSpring({
    opacity: displayContactsGraph ? 1 : 0,
    transform: `perspective(600px) rotateX(${
      displayContactsGraph ? 180 : 0
    }deg)`,

    config: { mass: 5, tension: 250, friction: 50 },
  });

  const [animatedScoreStartProps, _animatedScoreStartAPI] = useSpring(
    () => ({
      from: { transform: "translateX(100px)" },
      to: [{ transform: "translateX(0px)" }],
      config: { duration: "1000" },
    }),
    []
  );

  const [animatedScoreLatestProps, _animatedScoreLatestAPI] = useSpring(
    () => ({
      from: { transform: "translateX(-100px)" },
      to: [{ transform: "translateX(0px)" }],
      config: { duration: "1000" },
    }),
    []
  );

  return (
    props.scoreStats && (
      <>
        <a.div
          style={{
            opacity: opacity,
            transform,
            rotateX: "180deg",
            display: opacity.to((o) => `${o < 0.65 ? "none" : "inherit"}`),
          }}
          className={classes.crmHealthScore}
        >
          <Typography variant={"h3"} style={{ fontWeight: "bold" }}>
            Contacts History
          </Typography>
          <CRMHealthHistoryPanelBarSection
            changeMetrics={props.contactsTimeline.map((entry) => {
              return {
                ...entry,
                readableMonth: new Date(entry.date).toLocaleDateString(
                  "en-us",
                  {
                    year: "2-digit",
                    month: "short",
                  }
                ),
              };
            })}
            series={["total_contacts", "contacts_with_issues"]}
          />
          <Typography
            variant={"h5"}
            style={{
              fontWeight: "bold",
              color: "#777776",
              cursor: "pointer",
            }}
            onClick={() => setDisplayContactsGraph(false)}
          >
            Health Score
          </Typography>
        </a.div>
        <a.div
          style={{
            opacity: opacity.to((o) => 1 - o),
            transform,
            display: opacity.to((o) => `${o >= 0.65 ? "none" : "inherit"}`),
          }}
          className={classes.crmHealthScore}
        >
          <Typography
            variant={"h3"}
            style={{ fontWeight: "bold" }}
            gutterBottom
          >
            CRM Health Score
          </Typography>
          <div className={classes.healthScoreComponent}>
            <div className={classes.healthScoreBoxes}>
              <div className={classes.healthScoreBox}>
                <Typography
                  variant={"h4"}
                  style={{ fontWeight: "bold" }}
                  gutterBottom
                >
                  Start
                </Typography>
                <a.div style={animatedScoreStartProps}>
                  <Typography
                    variant={"h3"}
                    style={{
                      color: "white",
                      padding: "15px 10px",
                      borderRadius: "20px",
                      fontWeight: "bold",
                      ...getCrmScoreStyles(
                        props.scoreStats.start_stats
                          ? props.scoreStats.start_stats.score
                          : 0
                      ),
                    }}
                  >
                    {props.scoreStats.start_stats
                      ? props.scoreStats.start_stats.score
                      : 0}
                    %
                  </Typography>
                </a.div>
              </div>
              <div className={classes.healthScoreBox}>
                <Typography
                  variant={"h4"}
                  style={{ fontWeight: "bold" }}
                  gutterBottom
                >
                  Latest
                </Typography>
                <a.div style={animatedScoreLatestProps}>
                  <Typography
                    variant={"h3"}
                    style={{
                      color: "white",
                      padding: "15px 5px",
                      borderRadius: "20px",
                      fontWeight: "bold",
                      ...getCrmScoreStyles(
                        props.scoreStats.latest_stats
                          ? props.scoreStats.latest_stats.score
                          : 0
                      ),
                    }}
                  >
                    {props.scoreStats.latest_stats
                      ? props.scoreStats.latest_stats.score
                      : 0}
                    %
                  </Typography>
                </a.div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-around" }}>
              <HealthScoreExplain />
            </div>
          </div>

          <div style={{ display: "flex" }}>
            <Typography
              variant={"h5"}
              style={{
                fontWeight: "bold",
                color: "#777776",
                cursor: "pointer",
              }}
              onClick={() => setDisplayContactsGraph(true)}
            >
              View More
            </Typography>
          </div>
        </a.div>
      </>
    )
  );
}
